import { useState, useEffect } from 'react';
import { useMonitoringTags, MonitoringTag } from '@/hooks/useMonitoringTags';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Edit2, Trash2, Sparkles, Loader2 } from 'lucide-react';
import { TagBadge } from './TagBadge';
import { TagForm } from './TagForm';
import { SuggestedTags } from './SuggestedTags';
import { GeneratedTagsDialog } from './GeneratedTagsDialog';
import { toast } from 'sonner';

interface TagManagerProps {
  healthArea: string;
}

const CATEGORY_LABELS: Record<MonitoringTag['category'], string> = {
  lab: 'Exame Laboratorial',
  symptom: 'Sintoma',
  condition: 'Condição',
  medication: 'Medicação',
  vital_sign: 'Sinal Vital'
};

export function TagManager({ healthArea }: TagManagerProps) {
  const {
    tags,
    loading,
    generating,
    createTag,
    updateTag,
    deleteTag,
    getSuggestedTags,
    generateSuggestedTags
  } = useMonitoringTags();

  const [showForm, setShowForm] = useState(false);
  const [editingTag, setEditingTag] = useState<MonitoringTag | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showGeneratedDialog, setShowGeneratedDialog] = useState(false);
  const [generatedTags, setGeneratedTags] = useState<any[]>([]);

  useEffect(() => {
    if (!loading && tags.length === 0) {
      setShowSuggestions(true);
    }
  }, [loading, tags.length]);

  const suggestedTags = getSuggestedTags(healthArea);

  const handleCreateTags = async (newTags: Array<{ name: string, category: MonitoringTag['category'], color: string }>) => {
    try {
      for (const tag of newTags) {
        await createTag({
          ...tag,
          is_suggested: true
        });
      }
      setShowSuggestions(false);
    } catch (error) {
      // Error handled in hook
    }
  };

  const handleEdit = (tag: MonitoringTag) => {
    setEditingTag(tag);
    setShowForm(true);
  };

  const handleFormSubmit = async (tagData: Omit<MonitoringTag, 'id' | 'user_id' | 'created_at'>) => {
    if (editingTag) {
      await updateTag(editingTag.id, tagData);
    } else {
      await createTag(tagData);
    }
    setShowForm(false);
    setEditingTag(null);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir esta tag?')) {
      await deleteTag(id);
    }
  };

  const handleGenerateSuggestions = async () => {
    try {
      const suggestions = await generateSuggestedTags(healthArea);
      setGeneratedTags(suggestions);
      setShowGeneratedDialog(true);
    } catch (error) {
      // Error already handled in hook
    }
  };

  const handleAddGeneratedTags = async (selectedTags: any[]) => {
    try {
      const promises = selectedTags.map(tag =>
        createTag({
          name: tag.name,
          category: tag.category,
          color: tag.color,
          is_suggested: false
        })
      );

      await Promise.all(promises);
      toast.success(`${selectedTags.length} tags adicionadas com sucesso`);
    } catch (error) {
      console.error('Error adding generated tags:', error);
      toast.error('Erro ao adicionar algumas tags');
    }
  };

  const handleRegenerate = async () => {
    try {
      const suggestions = await generateSuggestedTags(healthArea);
      setGeneratedTags(suggestions);
    } catch (error) {
      // Error already handled in hook
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {showSuggestions && suggestedTags.length > 0 && (
        <SuggestedTags
          healthArea={healthArea}
          suggestions={suggestedTags}
          onAddSelected={handleCreateTags}
        />
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle>Minhas Tags</CardTitle>
              <CardDescription>
                Gerencie as tags para categorizar indicadores de saúde
              </CardDescription>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={handleGenerateSuggestions}
                disabled={generating}
                className="w-full sm:w-auto"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Gerando...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Sugerir Tags
                  </>
                )}
              </Button>
              <Button onClick={() => setShowForm(true)} className="w-full sm:w-auto">
                <Plus className="w-4 h-4 mr-2" />
                Nova Tag
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {tags.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p>Nenhuma tag criada ainda</p>
              <p className="text-sm mt-2">Use "Sugerir Tags" ou crie manualmente</p>
            </div>
          ) : (
            <div className="space-y-6">
              {['lab', 'symptom', 'condition', 'medication', 'vital_sign'].map((cat) => {
                const categoryTags = tags.filter(t => t.category === cat);
                if (categoryTags.length === 0) return null;

                return (
                  <div key={cat}>
                    <h3 className="text-sm font-semibold text-muted-foreground mb-3">
                      {CATEGORY_LABELS[cat as MonitoringTag['category']]}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {categoryTags.map((tag) => (
                        <div key={tag.id} className="flex items-center gap-1">
                          <TagBadge
                            name={tag.name}
                            color={tag.color}
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6"
                            onClick={() => handleEdit(tag)}
                          >
                            <Edit2 className="h-3 w-3" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 text-destructive"
                            onClick={() => handleDelete(tag.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <TagForm
        open={showForm}
        onOpenChange={(open) => {
          setShowForm(open);
          if (!open) setEditingTag(null);
        }}
        onSubmit={handleFormSubmit}
        initialData={editingTag || undefined}
      />

      <GeneratedTagsDialog
        open={showGeneratedDialog}
        onOpenChange={setShowGeneratedDialog}
        tags={generatedTags}
        healthArea={healthArea}
        onAddTags={handleAddGeneratedTags}
        onRegenerate={handleRegenerate}
        isRegenerating={generating}
      />
    </div>
  );
}
